import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { ProtectedRoute } from "./components/ProtectedRoute";

// Pages
import { Home } from "./pages/Home";
import { Login } from "./pages/Login";
import { Register } from "./pages/Register";
import { Contact } from "./pages/Contact";
import { Properties } from "./pages/Properties";
import { PropertyDetail } from "./pages/PropertyDetail";
import { ReportIssue } from "./pages/ReportIssue";
import { TenantDashboard } from "./pages/TenantDashboard";
import { TenantExplore } from "./pages/TenantExplore";
import { AdminDashboard } from "./pages/AdminDashboard";
import { LandlordDashboard } from "./pages/LandlordDashboard";
import { Profile } from "./pages/Profile";
import { BillingSettings } from "./pages/BillingSettings";
import { ForgotPassword, ResetPassword } from "./pages/PasswordReset";
import { VerifyEmail } from "./pages/VerifyEmail";
import { VerifyRegistrationEmail } from "./pages/VerifyRegistrationEmail";
import { PropertyDocuments } from "./pages/PropertyDocuments";
import { ComplianceDashboard } from "./pages/ComplianceDashboard";
import { LandlordHealthCheck } from "./pages/LandlordHealthCheck";
import { MtdGuide } from "./pages/MtdGuide";
import { UserGuide } from "./pages/UserGuide";
import { TermsOfService } from "./pages/TermsOfService";
import { SendDocuments } from './pages/SendDocuments';
import { WrittenStatement } from './pages/WrittenStatement';
import { SignDocument } from './pages/SignDocument';
import { AcknowledgeDocument } from './pages/AcknowledgeDocument';
import { MessageCenter } from "./pages/Messages/MessageCenter";
import { Loader2 } from 'lucide-react';
import { MessageThread } from "./pages/Messages/MessageThread";
import { SiteSettingsProvider, useSiteSettings } from './contexts/SiteSettingsContext';
import ListingBuilder from './pages/ListingBuilder';

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);

  return null;
}

function SendDocumentsRoute() {
  const { loading, publicDocumentDispatchEnabled } = useSiteSettings();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-royal-600" />
      </div>
    );
  }

  if (publicDocumentDispatchEnabled) {
    return <SendDocuments />;
  }

  return (
    <ProtectedRoute allowedRoles={['landlord', 'admin']}>
      <SendDocuments />
    </ProtectedRoute>
  );
}
function App() {
  const [showUpdateBanner, setShowUpdateBanner] = useState(false);
  const [swRegistration, setSwRegistration] = useState(null);

  useEffect(() => {
    const onSwUpdateReady = (event) => {
      const registration = event?.detail?.registration;
      if (registration) {
        setSwRegistration(registration);
      }
      setShowUpdateBanner(true);
    };

    window.addEventListener("swUpdateReady", onSwUpdateReady);
    return () => {
      window.removeEventListener("swUpdateReady", onSwUpdateReady);
    };
  }, []);

  const applyServiceWorkerUpdate = () => {
    const waitingWorker = swRegistration?.waiting;
    if (!waitingWorker) {
      window.location.reload();
      return;
    }

    waitingWorker.postMessage({ type: "SKIP_WAITING" });
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (window.__swReloaded) {
        return;
      }
      window.__swReloaded = true;
      window.location.reload();
    });
  };

  return (
    <div className="App">
      {showUpdateBanner && (
        <div className="bg-navy-900 text-white px-4 py-3 text-sm flex items-center justify-between gap-4">
          <span>A new version is available - click to update</span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="px-3 py-1 rounded border border-white/40 hover:bg-white/10"
              onClick={applyServiceWorkerUpdate}
            >
              Update
            </button>
            <button
              type="button"
              className="px-3 py-1 rounded border border-white/20 hover:bg-white/10"
              onClick={() => setShowUpdateBanner(false)}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}
      <AuthProvider>
        <SiteSettingsProvider>
          <BrowserRouter>
            <ScrollToTop />
            <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/send-documents" element={<SendDocumentsRoute />} />
            <Route path="/landlord-health-check" element={<LandlordHealthCheck />} />
            <Route path="/mtd-guide" element={<MtdGuide />} />
            <Route path="/user-guide" element={<UserGuide />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/properties" element={<Navigate to="/tenant/explore" replace />} />
            <Route path="/properties/:id" element={<PropertyDetail />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/verify-registration" element={<VerifyRegistrationEmail />} />
            <Route path="/sign/:token" element={<SignDocument />} />
            <Route path="/acknowledge/:ack_token" element={<AcknowledgeDocument />} />

            {/* Profile Route (All authenticated users) */}
            <Route 
              path="/profile" 
              element={
                <ProtectedRoute allowedRoles={['admin', 'landlord', 'tenant']}>
                  <Profile />
                </ProtectedRoute>
              } 
            />
            <Route
              path="/settings/billing"
              element={<Navigate to="/profile" replace />}
            />
            <Route
              path="/messages"
              element={
                <ProtectedRoute allowedRoles={['landlord', 'tenant']}>
                  <MessageCenter />
                </ProtectedRoute>
              }
            />
            <Route
              path="/messages/:threadId"
              element={
                <ProtectedRoute allowedRoles={['landlord', 'tenant']}>
                  <MessageThread />
                </ProtectedRoute>
              }
            />

            {/* Tenant Routes */}
            <Route 
              path="/report-issue" 
              element={
                <ProtectedRoute allowedRoles={['tenant']}>
                  <ReportIssue />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/tenant" 
              element={
                <ProtectedRoute allowedRoles={['tenant']}>
                  <TenantDashboard />
                </ProtectedRoute>
              } 
            />
            <Route
              path="/tenant/explore"
              element={
                <ProtectedRoute allowedRoles={['tenant']}>
                  <TenantExplore />
                </ProtectedRoute>
              }
            />

            {/* Written Statement */}
            <Route
              path="/tenancy-agreement"
              element={
                <ProtectedRoute allowedRoles={['landlord']}>
                  <WrittenStatement />
                </ProtectedRoute>
              }
            />

            {/* Landlord Dashboard */}
            <Route
              path="/landlord"
              element={
                <ProtectedRoute allowedRoles={['landlord']}>
                  <LandlordDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/landlord/listing-builder/:propertyId"
              element={
                <ProtectedRoute allowedRoles={['landlord']}>
                  <ListingBuilder />
                </ProtectedRoute>
              }
            />
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute allowedRoles={['landlord']}>
                  <LandlordDashboard />
                </ProtectedRoute>
              } 
            />

            {/* Landlord Document Routes */}
            <Route 
              path="/compliance" 
              element={
                <ProtectedRoute allowedRoles={['landlord', 'admin']}>
                  <ComplianceDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/property/:propertyId/documents" 
              element={
                <ProtectedRoute allowedRoles={['landlord', 'admin']}>
                  <PropertyDocuments />
                </ProtectedRoute>
              } 
            />

            {/* Admin Dashboard */}
            <Route 
              path="/admin" 
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AdminDashboard />
                </ProtectedRoute>
              } 
            />
            <Route
              path="/admin/engagement"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <Navigate to="/admin#engagement" replace />
                </ProtectedRoute>
              }
            />

            {/* Fallback */}
            <Route path="*" element={<Home />} />
            </Routes>
          </BrowserRouter>
        </SiteSettingsProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
